import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FlagIcon from '../../../components/FlagIcon';
import {
  findCurrenciesMatchingQuery,
  getCurrencyMatchLabel,
} from '../../../utils/currencyUtils';
import {
  buildLastSentMap,
  getBankLabel,
  getCountryInfo,
  getInitials,
  getLastSentDate,
  isBusinessRecipient,
} from '../../../utils/recipientHelpers';

const CARD_CLASS = 'rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)]';
const ITEMS_PER_PAGE = 6;

function RecipientFlagBadge({ currencyCode, size = 'md' }) {
  const sizeClass =
    size === 'sm'
      ? 'h-5 w-5 rounded-[10px]'
      : 'h-9 w-9 rounded-[12px]';
  const iconClass = size === 'sm' ? 'h-2.5 w-3.5' : 'h-4 w-5';

  return (
    <span
      className={`flex shrink-0 items-center justify-center border border-[var(--color-border)] bg-[var(--off)] ${sizeClass}`}
      aria-hidden='true'
    >
      <FlagIcon currencyCode={currencyCode} className={`${iconClass} rounded-sm`} />
    </span>
  );
}

RecipientFlagBadge.propTypes = {
  currencyCode: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
};

RecipientFlagBadge.defaultProps = {
  currencyCode: '',
  size: 'md',
};

function RecipientListButton({ recipient, countryInfo, isSelected, isRecent, onSelect }) {
  const isBusiness = isBusinessRecipient(recipient);
  const bankLabel = getBankLabel(recipient);
  const bankSuffix = bankLabel && bankLabel !== '—' ? ` · ${bankLabel}` : '';

  if (isRecent) {
    return (
      <button
        type='button'
        onClick={() => onSelect(recipient.id)}
        className={`flex w-full items-center gap-3 rounded-[10px] border px-3.5 py-3 text-left transition-colors hover:bg-[var(--off)] ${
          isSelected ? 'border-[var(--n800)] bg-[var(--off)]' : 'border-[var(--color-border)] bg-transparent'
        }`}
      >
        <RecipientFlagBadge currencyCode={recipient.currency} />
        <div className='min-w-0 flex-1'>
          <div className='text-sm font-medium text-[var(--n800)]'>{recipient.name || '—'}</div>
          <div className='text-xs text-[var(--text-tertiary)]'>{countryInfo.name}</div>
        </div>
        <span className='text-[11px] font-semibold tracking-[0.05em] text-[var(--text-tertiary)]'>{recipient.currency || '—'}</span>
      </button>
    );
  }

  return (
    <button
      type='button'
      onClick={() => onSelect(recipient.id)}
      className={`flex w-full items-center gap-3 rounded-[10px] border-b border-[var(--color-border)] px-3 py-3 text-left transition-colors last:border-b-0 hover:bg-[var(--off)] ${
        isSelected ? 'bg-[var(--off)]' : 'bg-transparent'
      }`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold font-[family-name:var(--font-ui)] ${
          isBusiness ? 'bg-[var(--n900)] text-white' : 'border border-[var(--color-border)] bg-[var(--off)] text-[var(--n700)]'
        }`}
      >
        {getInitials(recipient.name)}
      </div>
      <div className='min-w-0 flex-1'>
        <div className='text-sm font-medium text-[var(--n800)]'>{recipient.name || '—'}</div>
        <div className='flex min-w-0 items-center gap-1.5 text-xs text-[var(--text-tertiary)]'>
          <RecipientFlagBadge currencyCode={recipient.currency} size='sm' />
          <span className='truncate'>{countryInfo.name}</span>
          {bankSuffix && <span className='truncate opacity-55'>{bankSuffix}</span>}
        </div>
      </div>
      <span className='shrink-0 text-[11px] font-semibold tracking-[0.05em] text-[var(--text-tertiary)]'>{recipient.currency || '—'}</span>
    </button>
  );
}

const recipientShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  currency: PropTypes.string,
});

const countryInfoShape = PropTypes.shape({
  code: PropTypes.string,
  name: PropTypes.string,
  flag: PropTypes.string,
});

RecipientListButton.propTypes = {
  recipient: recipientShape.isRequired,
  countryInfo: countryInfoShape.isRequired,
  isSelected: PropTypes.bool,
  isRecent: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

function matchesCurrencyFilter(recipient, currencyFilter) {
  if (!currencyFilter) return true;
  return String(recipient.currency || '').toUpperCase() === String(currencyFilter).toUpperCase();
}

function CurrencyMatchButton({ match, onSelect }) {
  const label = getCurrencyMatchLabel(match);
  let primaryLabel = match.code;
  let secondaryLabel = match.name || match.code;
  if (match.matchedByCountry && match.countryName) {
    primaryLabel = match.countryName;
    secondaryLabel = `Add ${match.code} recipient`;
  } else if (match.countryName) {
    secondaryLabel = match.countryName;
  }

  return (
    <button
      type='button'
      onClick={() => onSelect(match.code)}
      className='flex w-full items-center gap-3.5 rounded-[12px] border-2 border-[var(--n800)] bg-[var(--white)] px-4 py-3.5 text-left transition hover:bg-[var(--off)]'
    >
      <RecipientFlagBadge currencyCode={match.code} />
      <div className='min-w-0 flex-1'>
        <div className='text-[15px] font-semibold text-[var(--n900)] font-[family-name:var(--font-ui)]'>{primaryLabel}</div>
        <div className='mt-0.5 text-xs text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>{secondaryLabel}</div>
      </div>
      <span className='sr-only'>{label}</span>
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='var(--n800)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <path d='M9 18l6-6-6-6' />
      </svg>
    </button>
  );
}

CurrencyMatchButton.propTypes = {
  match: PropTypes.shape({
    code: PropTypes.string.isRequired,
    name: PropTypes.string,
    countryName: PropTypes.string,
    matchedByCountry: PropTypes.bool,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

function RecipientSelectStep({
  recipients,
  transactions,
  configCurrencies,
  selectedRecipientId,
  gettingRecipients,
  onSelect,
  onAddNew,
  currencyFilter,
  matchNotice,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const currencyFilteredRecipients = useMemo(
    () => recipients.filter((recipient) => matchesCurrencyFilter(recipient, currencyFilter)),
    [recipients, currencyFilter],
  );

  const lastSentMap = useMemo(() => buildLastSentMap(transactions), [transactions]);

  const recentRecipients = useMemo(
    () =>
      [...currencyFilteredRecipients]
        .map((recipient) => ({ recipient, date: getLastSentDate(recipient, lastSentMap) }))
        .filter((item) => item.date)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 2)
        .map((item) => item.recipient),
    [currencyFilteredRecipients, lastSentMap],
  );

  const filteredRecipients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return currencyFilteredRecipients;
    return currencyFilteredRecipients.filter((recipient) => {
      const country = getCountryInfo(recipient, configCurrencies);
      const bank = getBankLabel(recipient).toLowerCase();
      return (
        String(recipient.name || '')
          .toLowerCase()
          .includes(query) ||
        String(recipient.currency || '')
          .toLowerCase()
          .includes(query) ||
        country.name.toLowerCase().includes(query) ||
        bank.includes(query)
      );
    });
  }, [currencyFilteredRecipients, searchQuery, configCurrencies]);

  const matchedCurrencies = useMemo(() => {
    const query = searchQuery.trim();
    if (!query) {
      if (!currencyFilter) return [];
      return findCurrenciesMatchingQuery(configCurrencies, currencyFilter, { limit: 3 });
    }
    const matches = findCurrenciesMatchingQuery(configCurrencies, query, { limit: 6 });
    if (!currencyFilter) return matches;
    const filterCode = String(currencyFilter).toUpperCase();
    const filtered = matches.filter((match) => match.code.toUpperCase() === filterCode);
    return filtered.length > 0 ? filtered : matches;
  }, [configCurrencies, currencyFilter, searchQuery]);

  const totalItems = filteredRecipients.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRecipients = filteredRecipients.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const startItem = totalItems === 0 ? 0 : startIndex + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, '…', totalPages];
    if (currentPage >= totalPages - 2) return [1, '…', totalPages - 2, totalPages - 1, totalPages];
    return [1, '…', currentPage, '…', totalPages];
  }, [currentPage, totalPages]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const emptyMessage = (() => {
    if (recipients.length === 0) return 'No recipients yet. Add one to send money.';
    if (currencyFilter && currencyFilteredRecipients.length === 0) {
      return `No recipients receive ${currencyFilter}. Add one or choose a different currency.`;
    }
    if (currencyFilter) return `No ${currencyFilter} recipients match your search.`;
    return 'No recipients match your search.';
  })();

  const handleAddCurrency = (currencyCode) => {
    onAddNew(currencyCode);
  };

  return (
    <div className='grid gap-[18px]'>
      {matchNotice && (
        <p className='rounded-lg border border-[#f5d0a8] bg-[#fff8ef] px-4 py-3 text-[13px] leading-snug text-[#8a4b12] font-[family-name:var(--font-ui)]'>
          {matchNotice}
        </p>
      )}
      {currencyFilter && (
        <p className='rounded-lg border border-[var(--color-border)] bg-[var(--off)] px-4 py-3 text-[13px] text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
          Showing recipients who can receive <strong className='font-medium text-[var(--n800)]'>{currencyFilter}</strong> from the calculator.
        </p>
      )}
      {recentRecipients.length > 0 && (
        <div className={`${CARD_CLASS} p-6`}>
          <div className='mb-3.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]'>
            <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' aria-hidden='true'>
              <circle cx='12' cy='12' r='10' />
              <path d='M12 6v6l4 2' />
            </svg>
            Recent recipients
          </div>
          <div className='flex flex-col gap-1.5'>
            {recentRecipients.map((recipient) => (
              <RecipientListButton
                key={recipient.id}
                recipient={recipient}
                countryInfo={getCountryInfo(recipient, configCurrencies)}
                isSelected={selectedRecipientId === recipient.id}
                isRecent
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      )}

      <div className={`${CARD_CLASS} p-6`}>
        <div className='relative mb-[18px]'>
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='var(--text-tertiary)'
            strokeWidth='1.8'
            className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2'
            aria-hidden='true'
          >
            <circle cx='11' cy='11' r='8' />
            <path d='M21 21l-4.35-4.35' />
          </svg>
          <input
            type='search'
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder='Search by name, country or currency'
            className='w-full rounded-lg border border-[var(--color-border)] bg-[var(--white)] py-3 pl-11 pr-3.5 text-sm text-[var(--n900)] outline-none placeholder:text-[var(--text-tertiary)] focus:ring-2 focus:ring-[var(--n200)]'
          />
        </div>

        <div className='mb-3 flex items-center justify-between gap-3'>
          <div className='text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]'>
            All recipients · {currencyFilteredRecipients.length}
          </div>
        </div>

        {gettingRecipients && (
          <div className='space-y-2'>
            {[0, 1, 2, 3, 4, 5].map((key) => (
              <div key={key} className='h-14 animate-pulse rounded-[10px] bg-[var(--off)]' />
            ))}
          </div>
        )}

        {!gettingRecipients && totalItems === 0 && (
          <div className='flex flex-col items-center px-2 py-8 text-center'>
            <p className='text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>{emptyMessage}</p>

            {matchedCurrencies.length > 0 && (
              <div className='mt-5 w-full max-w-md space-y-2.5'>
                <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]'>
                  Add for this currency
                </p>
                {matchedCurrencies.map((match) => (
                  <CurrencyMatchButton key={match.code} match={match} onSelect={handleAddCurrency} />
                ))}
              </div>
            )}

            <button
              type='button'
              onClick={() => onAddNew()}
              className='mt-5 inline-flex min-h-12 w-full max-w-md items-center justify-center gap-2 rounded-full bg-[var(--n900)] px-6 text-[15px] font-semibold text-white transition hover:bg-[var(--n800)] font-[family-name:var(--font-ui)]'
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' aria-hidden='true'>
                <path d='M12 5v14M5 12h14' />
              </svg>
              Add new recipient
            </button>
          </div>
        )}

        {!gettingRecipients && totalItems > 0 && (
          <>
            {searchQuery.trim() && matchedCurrencies.length > 0 && (
              <div className='mb-4 space-y-2 rounded-[12px] border border-dashed border-[var(--color-border)] bg-[var(--off)] p-3'>
                <p className='px-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]'>
                  Or add a new recipient
                </p>
                {matchedCurrencies.slice(0, 3).map((match) => (
                  <CurrencyMatchButton key={match.code} match={match} onSelect={handleAddCurrency} />
                ))}
              </div>
            )}

            <div className='flex flex-col gap-0.5'>
              {paginatedRecipients.map((recipient) => (
                <RecipientListButton
                  key={recipient.id}
                  recipient={recipient}
                  countryInfo={getCountryInfo(recipient, configCurrencies)}
                  isSelected={selectedRecipientId === recipient.id}
                  onSelect={onSelect}
                />
              ))}
            </div>

            <div className='mt-3.5 flex flex-wrap items-center justify-between gap-3'>
              <p className='text-xs tabular-nums text-[var(--text-tertiary)]'>
                Showing <strong className='font-medium text-[var(--n800)]'>{startItem}–{endItem}</strong> of{' '}
                <strong className='font-medium text-[var(--n800)]'>{totalItems}</strong> recipients
              </p>
              <div className='flex items-center gap-1'>
                <button
                  type='button'
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className='inline-flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-2.5 text-[12.5px] font-medium text-[var(--n800)] disabled:cursor-not-allowed disabled:opacity-40'
                >
                  <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <path d='M15 18l-6-6 6-6' />
                  </svg>
                  Prev
                </button>
                {pageNumbers.map((page) =>
                  page === '…' ? (
                    <span key='send-money-ellipsis' className='px-1 text-sm text-[var(--text-tertiary)]'>
                      …
                    </span>
                  ) : (
                    <button
                      key={page}
                      type='button'
                      onClick={() => setCurrentPage(page)}
                      className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2.5 text-[12.5px] font-medium tabular-nums ${
                        currentPage === page
                          ? 'border border-[var(--n900)] bg-[var(--n900)] text-white'
                          : 'border border-[var(--color-border)] bg-[var(--white)] text-[var(--n800)]'
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}
                <button
                  type='button'
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className='inline-flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-2.5 text-[12.5px] font-medium text-[var(--n800)] disabled:cursor-not-allowed disabled:opacity-40'
                >
                  Next
                  <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <path d='M9 18l6-6-6-6' />
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

RecipientSelectStep.propTypes = {
  recipients: PropTypes.arrayOf(recipientShape).isRequired,
  transactions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  configCurrencies: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      country: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      countries: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
    }),
  ),
  selectedRecipientId: PropTypes.string,
  gettingRecipients: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onAddNew: PropTypes.func.isRequired,
  currencyFilter: PropTypes.string,
  matchNotice: PropTypes.string,
};

RecipientSelectStep.defaultProps = {
  currencyFilter: null,
  matchNotice: '',
};

export default RecipientSelectStep;
